/**
 * brioCAST Advertiser Dashboard & Campaign Manager
 * Drop-in for existing CRA portal
 *
 * Add to renderPage():
 *   case "ad-dashboard": return hasCap(account, "AD_BUYER") ? <AdDashboardPage account={account} /> : <NoCap cap="AD_BUYER" />;
 * Add to Sidebar:
 *   { id: "ad-dashboard", label: "廣告投放", icon: "📢", cap: "AD_BUYER" }
 */
import { useState, useEffect } from "react";

const API_V1 = "https://api.briolabs.io/api/v1";
async function apiV1(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers };
  const res = await fetch(`${API_V1}${path}`, { ...options, headers, body: options.body ? JSON.stringify(options.body) : undefined });
  const data = await res.json();
  if (!res.ok && !data.success) throw new Error(data.error || "Request failed");
  return data;
}

const t = {
  card: "#1A1D27", border: "#2A2E3A", accent: "#6C5CE7", accentLight: "#A29BFE",
  success: "#00B894", danger: "#E17055", warning: "#FDCB6E",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
  bg: "#0F1117",
};

const statusMeta = {
  draft: { label: "草稿", color: t.textDim, bg: "rgba(90,94,114,0.15)" },
  pending_review: { label: "審核中", color: t.warning, bg: "rgba(253,203,110,0.12)" },
  active: { label: "投放中", color: t.success, bg: "rgba(0,184,148,0.12)" },
  paused: { label: "已暫停", color: t.textMuted, bg: "rgba(139,143,163,0.12)" },
  rejected: { label: "已退回", color: t.danger, bg: "rgba(225,112,85,0.12)" },
  completed: { label: "已完成", color: t.accentLight, bg: "rgba(162,155,254,0.12)" },
  archived: { label: "已封存", color: t.textDim, bg: "rgba(90,94,114,0.1)" },
};

function StatBox({ label, value, sub, icon, color }) {
  return (
    <div style={{ background: t.card, borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)", padding: "18px 20px", flex: "1 1 180px", minWidth: 150 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>{label}</div>
        {icon && <span style={{ fontSize: 22, opacity: 0.5 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || t.text }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: t.textDim, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function CampaignRow({ campaign, onSelect }) {
  const sm = statusMeta[campaign.status] || statusMeta.draft;
  const budget = Number(campaign.total_budget_ntd || 0);
  const spent = Number(campaign.spent_amount_ntd || 0);
  const pct = budget > 0 ? Math.min(100, (spent / budget * 100)).toFixed(1) : 0;

  return (
    <div onClick={() => onSelect(campaign)} style={{
      display: "grid", gridTemplateColumns: "1fr 100px 120px 100px 80px", gap: 0,
      padding: "14px 18px", alignItems: "center", cursor: "pointer",
      borderBottom: "1px solid rgba(255,255,255,0.04)", transition: "background 0.12s",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{campaign.name}</div>
        <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>
          {campaign.name_zh || ""} · {campaign.company_name || campaign.advertiser_id}
        </div>
      </div>
      <div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999, color: sm.color, background: sm.bg }}>{sm.label}</span>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace" }}>NT$ {budget.toLocaleString()}</div>
        <div style={{ width: "100%", height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 4 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: spent > budget * 0.8 ? t.warning : t.accent, borderRadius: 2, transition: "width 0.3s" }} />
        </div>
        <div style={{ fontSize: 10, color: t.textDim, marginTop: 2 }}>{pct}% 已消耗</div>
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>
        {campaign.start_date?.slice(5) || ""} → {campaign.end_date?.slice(5) || ""}
      </div>
      <div style={{ textAlign: "right", fontSize: 18, color: t.textDim }}>›</div>
    </div>
  );
}

export default function AdDashboardPage({ account }) {
  const [view, setView] = useState("list"); // "list" | "detail" | "create"
  const [dashboard, setDashboard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Campaign creation state ───
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", name_zh: "", description: "",
    total_budget_ntd: 10000, daily_budget_ntd: "",
    start_date: "", end_date: "",
    venue_categories: [], target_cities: [],
    cpm_rate: 50, campaign_type: "paid_local",
    frequency_cap_daily: 3,
  });
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState("");

  const fetchData = async () => {
    try {
      const [dashRes, campRes] = await Promise.all([
        apiV1("/advertiser/dashboard"),
        apiV1("/advertiser/campaigns"),
      ]);
      setDashboard(dashRes.data);
      setCampaigns(campRes.data.campaigns || []);
    } catch (err) {
      console.error("[AdDashboard]", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.total_budget_ntd || !form.start_date || !form.end_date) {
      setToast("請填寫活動名稱、預算與日期");
      return;
    }
    setCreating(true);
    try {
      await apiV1("/advertiser/campaigns", { method: "POST", body: form });
      setToast(`✓ ${form.name} 已建立`);
      setView("list");
      setStep(1);
      setForm({ name: "", name_zh: "", description: "", total_budget_ntd: 10000, daily_budget_ntd: "", start_date: "", end_date: "", venue_categories: [], target_cities: [], cpm_rate: 50, campaign_type: "paid_local", frequency_cap_daily: 3 });
      fetchData();
    } catch (err) { setToast("建立失敗: " + err.message); }
    finally { setCreating(false); }
  };

  const handleAction = async (campId, action) => {
    try {
      await apiV1(`/advertiser/campaigns/${campId}/${action}`, { method: "POST" });
      fetchData();
      setToast(`✓ 操作成功`);
    } catch (err) { setToast(err.message); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: t.textMuted }}>載入中...</div>;

  const inputStyle = { width: "100%", padding: "11px 14px", background: "#141720", border: `1px solid ${t.border}`, borderRadius: 10, color: t.text, fontSize: 14, outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 13, color: t.textMuted, marginBottom: 6, display: "block", fontWeight: 500 };
  const btnStyle = { padding: "10px 20px", borderRadius: 10, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer" };

  const VENUES = [
    { id: "clinic", label: "🏥 診所" }, { id: "restaurant", label: "🍔 餐飲" },
    { id: "temple", label: "⛩️ 寺廟" }, { id: "parking", label: "🅿 停車場" },
    { id: "fitness", label: "💪 健身房" }, { id: "ev_charging", label: "⚡ EV充電" },
    { id: "postpartum", label: "🤱 月子中心" }, { id: "retail", label: "🛍 零售" },
  ];
  const CITIES = [
    { id: "taipei", label: "台北市" }, { id: "new_taipei", label: "新北市" },
    { id: "taoyuan", label: "桃園市" }, { id: "taichung", label: "台中市" },
    { id: "tainan", label: "台南市" }, { id: "kaohsiung", label: "高雄市" },
  ];

  const toggleArray = (arr, val) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  // ═══ CAMPAIGN DETAIL VIEW ═══
  if (view === "detail" && selected) {
    const sm = statusMeta[selected.status] || statusMeta.draft;
    const targeting = typeof selected.targeting === "string" ? JSON.parse(selected.targeting || "{}") : (selected.targeting || {});

    return (
      <div>
        <button onClick={() => { setView("list"); setSelected(null); }} style={{ ...btnStyle, background: "transparent", color: t.textMuted, border: `1px solid ${t.border}`, marginBottom: 16, fontSize: 13 }}>← 返回列表</button>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>{selected.name}</h2>
            <p style={{ color: t.textMuted, fontSize: 13, margin: 0 }}>{selected.name_zh} · {selected.company_name}</p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 999, color: sm.color, background: sm.bg }}>{sm.label}</span>
            {selected.status === "draft" && <button onClick={() => handleAction(selected.id, "submit")} style={{ ...btnStyle, background: t.accent, color: "#fff", fontSize: 12, padding: "5px 14px" }}>送審</button>}
            {selected.status === "active" && <button onClick={() => handleAction(selected.id, "pause")} style={{ ...btnStyle, background: "transparent", color: t.warning, border: `1px solid ${t.warning}40`, fontSize: 12, padding: "5px 14px" }}>暫停</button>}
            {selected.status === "paused" && <button onClick={() => handleAction(selected.id, "resume")} style={{ ...btnStyle, background: t.success, color: "#fff", fontSize: 12, padding: "5px 14px" }}>恢復</button>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ background: t.card, borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)", padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>💰 預算與消耗</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: t.textMuted }}>總預算</span>
              <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace" }}>NT$ {Number(selected.total_budget_ntd).toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: t.textMuted }}>日預算</span>
              <span style={{ fontSize: 13, fontFamily: "monospace" }}>NT$ {Number(selected.daily_budget_ntd).toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: t.textMuted }}>已消耗</span>
              <span style={{ fontSize: 13, fontFamily: "monospace", color: t.warning }}>NT$ {Number(selected.spent_amount_ntd).toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: t.textMuted }}>CPM</span>
              <span style={{ fontSize: 13, fontFamily: "monospace" }}>NT$ {selected.cpm_rate}</span>
            </div>
          </div>
          <div style={{ background: t.card, borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)", padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>🎯 投放設定</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>場景: <span style={{ color: t.text }}>{(targeting.scenes || targeting.venue_categories || []).join(", ") || "全場景"}</span></div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>城市: <span style={{ color: t.text }}>{(targeting.cities || []).join(", ") || "全台"}</span></div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>日期: <span style={{ color: t.text }}>{selected.start_date} → {selected.end_date}</span></div>
            <div style={{ fontSize: 12, color: t.textMuted }}>頻次上限: <span style={{ color: t.text }}>{selected.frequency_cap_daily || 3}次/日</span></div>
          </div>
        </div>

        <div style={{ background: t.card, borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)", padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>📊 成效</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {[
              { l: "已曝光", v: selected.impressions_delivered || 0 },
              { l: "保證曝光", v: selected.impressions_guaranteed || 0 },
              { l: "排程方式", v: selected.pacing_target || "even" },
              { l: "優先順序", v: selected.priority || 5 },
            ].map((s) => (
              <div key={s.l} style={{ minWidth: 80 }}>
                <div style={{ fontSize: 11, color: t.textDim }}>{s.l}</div>
                <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{typeof s.v === "number" ? s.v.toLocaleString() : s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ═══ CAMPAIGN WIZARD ═══
  if (view === "create") {
    return (
      <div>
        <button onClick={() => { setView("list"); setStep(1); }} style={{ ...btnStyle, background: "transparent", color: t.textMuted, border: `1px solid ${t.border}`, marginBottom: 16, fontSize: 13 }}>← 取消</button>

        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>📢 建立廣告活動</h2>
        <p style={{ color: t.textMuted, fontSize: 13, marginBottom: 24 }}>
          Step {step} / 3 — {step === 1 ? "基本資料" : step === 2 ? "投放設定" : "預算與排程"}
        </p>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? t.accent : "rgba(255,255,255,0.06)", transition: "background 0.3s" }} />
          ))}
        </div>

        <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", padding: 24 }}>
          {step === 1 && (<>
            <label style={labelStyle}>活動名稱 *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Spring Festival 2026" style={{ ...inputStyle, marginBottom: 16 }} />

            <label style={labelStyle}>活動名稱 (中文)</label>
            <input value={form.name_zh} onChange={(e) => setForm({ ...form, name_zh: e.target.value })} placeholder="e.g. 春節檔廣告" style={{ ...inputStyle, marginBottom: 16 }} />

            <label style={labelStyle}>說明</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="廣告活動描述..." rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 16 }} />

            <label style={labelStyle}>活動類型</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[{ id: "paid_local", label: "🏪 本地推廣" }, { id: "guaranteed", label: "🎯 保證曝光" }, { id: "house", label: "🏠 平台自用" }].map((ct) => (
                <button key={ct.id} onClick={() => setForm({ ...form, campaign_type: ct.id })} style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                  border: `1px solid ${form.campaign_type === ct.id ? `${t.accent}60` : t.border}`,
                  background: form.campaign_type === ct.id ? `${t.accent}15` : "transparent",
                  color: form.campaign_type === ct.id ? t.accentLight : t.textMuted,
                }}>{ct.label}</button>
              ))}
            </div>
          </>)}

          {step === 2 && (<>
            <label style={labelStyle}>投放場域 (複選)</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {VENUES.map((v) => (
                <button key={v.id} onClick={() => setForm({ ...form, venue_categories: toggleArray(form.venue_categories, v.id) })} style={{
                  padding: "7px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                  border: `1px solid ${form.venue_categories.includes(v.id) ? `${t.accent}60` : t.border}`,
                  background: form.venue_categories.includes(v.id) ? `${t.accent}15` : "transparent",
                  color: form.venue_categories.includes(v.id) ? t.accentLight : t.textMuted,
                }}>{v.label}</button>
              ))}
            </div>

            <label style={labelStyle}>目標城市 (複選)</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {CITIES.map((c) => (
                <button key={c.id} onClick={() => setForm({ ...form, target_cities: toggleArray(form.target_cities, c.id) })} style={{
                  padding: "7px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                  border: `1px solid ${form.target_cities.includes(c.id) ? `${t.success}60` : t.border}`,
                  background: form.target_cities.includes(c.id) ? `${t.success}15` : "transparent",
                  color: form.target_cities.includes(c.id) ? t.success : t.textMuted,
                }}>{c.label}</button>
              ))}
            </div>

            <label style={labelStyle}>每日頻次上限 (同一裝置)</label>
            <input type="number" value={form.frequency_cap_daily} onChange={(e) => setForm({ ...form, frequency_cap_daily: Number(e.target.value) })} style={{ ...inputStyle, width: 120 }} />
          </>)}

          {step === 3 && (<>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>總預算 (NTD) *</label>
                <input type="number" value={form.total_budget_ntd} onChange={(e) => setForm({ ...form, total_budget_ntd: Number(e.target.value) })} style={{ ...inputStyle, fontFamily: "monospace" }} />
              </div>
              <div>
                <label style={labelStyle}>日預算 (NTD)</label>
                <input type="number" value={form.daily_budget_ntd} onChange={(e) => setForm({ ...form, daily_budget_ntd: Number(e.target.value) })} placeholder="自動計算" style={{ ...inputStyle, fontFamily: "monospace" }} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>開始日期 *</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>結束日期 *</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} style={inputStyle} />
              </div>
            </div>

            <label style={labelStyle}>CPM 出價 (NTD)</label>
            <input type="number" value={form.cpm_rate} onChange={(e) => setForm({ ...form, cpm_rate: Number(e.target.value) })} style={{ ...inputStyle, width: 140, fontFamily: "monospace", marginBottom: 16 }} />

            {/* Budget preview */}
            <div style={{ background: "#141720", borderRadius: 10, padding: 14, marginTop: 8 }}>
              <div style={{ fontSize: 12, color: t.textDim, marginBottom: 8 }}>預估曝光</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: t.accentLight, fontFamily: "monospace" }}>
                {form.cpm_rate > 0 ? Math.round(form.total_budget_ntd / form.cpm_rate * 1000).toLocaleString() : "—"} 次
              </div>
              <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>
                CPM NT${form.cpm_rate} × {form.total_budget_ntd ? Math.round(form.total_budget_ntd / form.cpm_rate).toLocaleString() : 0} 千次
              </div>
            </div>
          </>)}
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} style={{ ...btnStyle, background: "transparent", color: t.textMuted, border: `1px solid ${t.border}` }}>上一步</button>
          ) : <div />}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} style={{ ...btnStyle, background: t.accent, color: "#fff" }}>下一步</button>
          ) : (
            <button onClick={handleCreate} disabled={creating} style={{ ...btnStyle, background: creating ? t.textDim : t.accent, color: "#fff" }}>
              {creating ? "⏳ 建立中..." : "🚀 建立廣告活動"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ═══ MAIN LIST VIEW ═══
  const d = dashboard || {};
  const active = campaigns.filter(c => c.status === "active").length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>📢 廣告投放管理</h2>
          <p style={{ color: t.textMuted, fontSize: 14 }}>管理廣告活動、素材與投放成效</p>
        </div>
        <button onClick={() => setView("create")} style={{ ...btnStyle, background: t.accent, color: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
          ＋ 建立廣告活動
        </button>
      </div>

      {toast && (
        <div onClick={() => setToast("")} style={{ padding: "10px 16px", marginBottom: 14, fontSize: 13, color: toast.startsWith("✓") ? t.success : t.danger, background: toast.startsWith("✓") ? `${t.success}12` : `${t.danger}12`, borderRadius: 10, cursor: "pointer" }}>
          {toast}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <StatBox label="廣告活動" value={d.campaigns || 0} sub={`${active} 投放中`} icon="📋" />
        <StatBox label="總預算" value={`NT$${Number(d.total_budget || 0).toLocaleString()}`} icon="💰" color={t.warning} />
        <StatBox label="已消耗" value={`NT$${Number(d.total_spent || 0).toFixed(0)}`} icon="📉" color={t.accent} />
        <StatBox label="總曝光" value={Number(d.total_impressions || 0).toLocaleString()} icon="👁" color={t.success} />
      </div>

      {/* Campaign list */}
      <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 120px 100px 80px", padding: "10px 18px", borderBottom: `1px solid ${t.border}`, background: "rgba(0,0,0,0.2)" }}>
          {["活動", "狀態", "預算", "日期", ""].map((h) => (
            <div key={h} style={{ fontSize: 11, fontWeight: 600, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
          ))}
        </div>

        {campaigns.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📢</div>
            <div>尚無廣告活動</div>
            <button onClick={() => setView("create")} style={{ ...btnStyle, background: t.accent, color: "#fff", marginTop: 12, fontSize: 13 }}>建立第一個廣告</button>
          </div>
        ) : campaigns.map((c) => (
          <CampaignRow key={c.id} campaign={c} onSelect={(camp) => { setSelected(camp); setView("detail"); }} />
        ))}
      </div>
    </div>
  );
}
